/**
 * GuestIcon3D — Reusable vibrant 3D icon wrapper for Patient Portal
 * Bright gradients, soft shadows, premium depth — matching reference style
 */
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type IconVariant = 'transport' | 'hotel' | 'tour' | 'request' | 'info' | 'neutral';
export type IconSize = 'sm' | 'md' | 'lg' | 'xl';

const VARIANT_STYLES: Record<IconVariant, string> = {
  transport:
    'from-[hsl(215,95%,55%)] to-[hsl(225,90%,65%)] shadow-[0_6px_18px_-3px_hsl(220,90%,50%,0.45)]',
  hotel:
    'from-[hsl(170,75%,42%)] to-[hsl(185,70%,52%)] shadow-[0_6px_18px_-3px_hsl(175,70%,38%,0.45)]',
  tour:
    'from-[hsl(28,92%,56%)] to-[hsl(38,90%,62%)] shadow-[0_6px_18px_-3px_hsl(30,90%,48%,0.45)]',
  request:
    'from-[hsl(270,70%,58%)] to-[hsl(290,65%,65%)] shadow-[0_6px_18px_-3px_hsl(275,65%,50%,0.45)]',
  info:
    'from-[hsl(200,90%,50%)] to-[hsl(210,85%,62%)] shadow-[0_6px_18px_-3px_hsl(205,85%,45%,0.45)]',
  neutral:
    'from-[hsl(220,15%,55%)] to-[hsl(220,12%,68%)] shadow-[0_6px_18px_-3px_hsl(220,12%,45%,0.35)]',
};

/** Lighter tint for nav inactive icons — just the bg color, no shadow */
export const VARIANT_TINT: Record<IconVariant, string> = {
  transport: 'bg-[hsl(215,90%,95%)] text-[hsl(215,95%,50%)]',
  hotel: 'bg-[hsl(170,70%,94%)] text-[hsl(170,75%,38%)]',
  tour: 'bg-[hsl(30,90%,94%)] text-[hsl(28,92%,50%)]',
  request: 'bg-[hsl(270,65%,95%)] text-[hsl(270,70%,52%)]',
  info: 'bg-[hsl(200,85%,94%)] text-[hsl(200,90%,45%)]',
  neutral: 'bg-[hsl(220,12%,94%)] text-[hsl(220,15%,50%)]',
};

const SIZE_STYLES: Record<IconSize, { container: string; icon: string }> = {
  sm: { container: 'h-9 w-9 rounded-xl', icon: 'h-4 w-4' },
  md: { container: 'h-11 w-11 rounded-xl', icon: 'h-5 w-5' },
  lg: { container: 'h-14 w-14 rounded-2xl', icon: 'h-7 w-7' },
  xl: { container: 'h-16 w-16 rounded-2xl', icon: 'h-8 w-8' },
};

interface GuestIcon3DProps {
  icon: LucideIcon;
  variant?: IconVariant;
  size?: IconSize;
  className?: string;
}

export default function GuestIcon3D({
  icon: Icon,
  variant = 'neutral',
  size = 'md',
  className,
}: GuestIcon3DProps) {
  const sizeStyle = SIZE_STYLES[size];

  return (
    <div
      className={cn(
        'relative flex items-center justify-center bg-gradient-to-br shrink-0',
        sizeStyle.container,
        VARIANT_STYLES[variant],
        className
      )}
    >
      {/* Top highlight for glass depth */}
      <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-b from-white/25 to-transparent pointer-events-none" />
      {/* Bottom edge for 3D lift */}
      <div className="absolute inset-x-0 bottom-0 h-[40%] rounded-[inherit] bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
      <Icon className={cn(sizeStyle.icon, 'text-white relative z-10 drop-shadow-sm')} strokeWidth={2.2} />
    </div>
  );
}

/** Map service module to icon variant */
export function moduleToVariant(module: string): IconVariant {
  switch (module) {
    case 'transport': return 'transport';
    case 'stays': case 'ACCOMMODATION': return 'hotel';
    case 'tours': case 'TOUR': return 'tour';
    case 'interpreters': return 'info';
    default: return 'neutral';
  }
}

/** Map service category to icon variant */
export function categoryToVariant(category: string): IconVariant {
  switch (category) {
    case 'TRANSPORT': case 'TRANSPORTATION': return 'transport';
    case 'ACCOMMODATION': return 'hotel';
    case 'TOUR': case 'TOUR_BOOKING': return 'tour';
    default: return 'neutral';
  }
}
